import { useState, useRef, useEffect } from "react";

const SYSTEM_PROMPT = `You are MedGuide — not a chatbot, but a knowledgeable, compassionate friend who happens to understand medicine deeply. You exist because too many people suffer alone in a diagnostic desert, getting bounced between doctors, dismissed, or told "everything looks normal" when something is clearly wrong. You are here to change that.

## Your Core Mission
Help people understand what might be happening in their body, feel genuinely heard, and walk away knowing exactly what to do next — including how to advocate for themselves with doctors.

## Your Personality
- Warm, calm, and unhurried. Never clinical. Never robotic.
- You take people seriously. You never minimize or dismiss.
- You speak like a trusted friend with medical knowledge — plain language, no jargon unless you explain it.
- You validate before you investigate: acknowledge what they're feeling before asking more questions.
- You are honest about uncertainty — "I can't know for sure, but here's what this could mean..."

## Your Diagnostic Method
Ask ONE question at a time. Never fire a list of questions. Think like a thoughtful doctor on their best day:

Round 1 — Understand the main symptom:
- What does it feel like exactly? (sharp, dull, burning, pressure, throbbing?)
- Where exactly is it? Does it move or radiate?

Round 2 — Timeline and pattern:
- When did it start? Was there anything unusual around that time?
- Is it constant or does it come and go? Getting better, worse, or staying the same?
- What makes it better? What makes it worse?

Round 3 — Associated symptoms:
- Any other symptoms alongside this one, even seemingly unrelated ones?
- How is your sleep, energy, appetite?
- Any recent changes in stress, diet, environment, medications?

Round 4 — Their history and context:
- Have you seen a doctor about this? What did they say?
- Have you been dismissed or told everything is normal even though something feels wrong?
- Any relevant health history, family history?

## After Gathering Enough (3-6 exchanges):
Synthesize thoughtfully:
1. Validate their experience first — "Based on everything you've shared, I want you to know this is real and worth taking seriously."
2. Offer 2-3 possible explanations ranked by likelihood, explained in plain language
3. Give clear next steps — self-care, urgent care, ER, or specialist (be specific about WHICH specialist)
4. Arm them to advocate for themselves — give them exact language to use with their doctor:
   - "When you see your doctor, you can say: 'I've been experiencing X for Y weeks, it's worse when Z, and I'd like to rule out [condition].'"
   - Suggest specific tests to ask about by name if relevant
5. Red flags to watch for — tell them exactly what would mean "go to the ER now"

## Emergency Protocol
If someone describes: chest pain + shortness of breath, signs of stroke (face drooping, arm weakness, speech difficulty), severe allergic reaction, uncontrolled bleeding, loss of consciousness, or suicidal thoughts — immediately and clearly say:
"Please call 911 or go to the nearest emergency room right now. Do not wait."

## What You Never Do
- Never give a definitive diagnosis
- Never make someone feel stupid or dramatic for their symptoms
- Never rush to a conclusion before asking enough questions
- Never let someone feel alone in this

Start every conversation by greeting the user warmly, telling them they're in the right place, and asking what's been going on — leaving space for them to share as much or as little as they want.`;

const WELCOME = {
  role: "assistant",
  content: "Hey — I'm really glad you're here.\n\nI know how exhausting it can be to feel like something's wrong and not get real answers. This is a space where you won't be rushed, dismissed, or handed a generic response. We'll work through this together.\n\nSo — what's been going on? Tell me what you've been feeling. You can share as much or as little as you'd like to start.",
};

const STARTERS = [
  "I've had this for a while and no one can figure it out",
  "I've been dismissed by doctors before",
  "I'm not sure if this is serious",
  "I just need someone to actually listen",
];

function Dots() {
  return (
    <div style={{ display: "flex", gap: 5, padding: "13px 18px", alignItems: "center" }}>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 7, height: 7, borderRadius: "50%", background: "#7EB8A8",
          display: "inline-block",
          animation: `dp 1.3s ease-in-out ${i * 0.18}s infinite`,
        }} />
      ))}
    </div>
  );
}

export default function SamuelsonMedGuide() {
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const bottomRef = useRef(null);
  const taRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (taRef.current) {
      taRef.current.style.height = "auto";
      taRef.current.style.height = Math.min(taRef.current.scrollHeight, 130) + "px";
    }
  }, [input]);

  async function send(text) {
    const trimmed = (text ?? input).trim();
    if (!trimmed || loading) return;
    setStarted(true);

    const userMsg = { role: "user", content: trimmed };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch("/api/chat", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    messages: history.map(m => ({ role: m.role, content: m.content })),
  }),
});

      const data = await res.json();

      if (data.error) throw new Error(data.error.message);

      const reply = data?.content?.find(b => b.type === "text")?.text
        || "Something went wrong — please try again.";

      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Sorry, something went wrong: ${err.message}\n\nMake sure your API key is set correctly in the .env file.`,
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => taRef.current?.focus(), 80);
    }
  }

  const canSend = input.trim().length > 0 && !loading;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;1,400&family=Inter:wght@300;400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #E8F0ED; }
        @keyframes dp { 0%,80%,100%{transform:scale(.7);opacity:.4} 40%{transform:scale(1);opacity:1} }
        @keyframes up { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fi { from{opacity:0} to{opacity:1} }
        .msg { animation: up 0.28s ease forwards; }
        textarea { resize:none; font-family:'Inter',sans-serif; }
        textarea:focus { outline:none; }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-thumb { background:#B8D4CC; border-radius:3px; }
        ::-webkit-scrollbar-track { background:transparent; }
        .sb:hover { background:rgba(61,158,135,0.13)!important; border-color:#3D9E87!important; color:#2A7A65!important; transform:translateY(-1px); }
        .sndbtn:hover:not(:disabled) { transform:scale(1.06); box-shadow:0 6px 20px rgba(42,122,101,0.4)!important; }
        .sndbtn:active:not(:disabled) { transform:scale(0.95); }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(150deg,#E6F0ED 0%,#EEF4F2 40%,#F5F0EA 100%)",
        display: "flex", flexDirection: "column", alignItems: "center",
        fontFamily: "'Inter',sans-serif", padding: "20px 16px 24px",
      }}>
        {/* Header */}
        <div style={{ width: "100%", maxWidth: 660, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 42, height: 42,
                background: "linear-gradient(135deg,#3D9E87,#2A7A65)",
                borderRadius: 13, display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 21,
                boxShadow: "0 3px 14px rgba(42,122,101,0.28)",
              }}>🫀</div>
              <div>
                <h1 style={{
                  fontFamily: "'Lora',serif", fontSize: 25, fontWeight: 500,
                  color: "#1A3530", letterSpacing: "-0.3px", lineHeight: 1,
                }}>MedGuide</h1>
                <p style={{ fontSize: 12, color: "#6A9990", marginTop: 3, fontWeight: 300 }}>
                  Your symptom companion · Always in your corner
                </p>
              </div>
            </div>
            {started && (
              <button onClick={() => { setMessages([WELCOME]); setInput(""); setStarted(false); }} style={{
                background: "rgba(255,255,255,0.7)", border: "1px solid #C0D9D2",
                borderRadius: 20, padding: "6px 16px", fontSize: 12, color: "#4A8A80",
                cursor: "pointer", fontFamily: "'Inter',sans-serif", fontWeight: 500,
                backdropFilter: "blur(4px)", transition: "all 0.2s",
              }}>Start over</button>
            )}
          </div>
          <div style={{
            marginTop: 12, background: "linear-gradient(135deg,#FFF5F5,#FFF0F0)",
            border: "1px solid #F5C5C0", borderRadius: 10, padding: "9px 16px",
            fontSize: 12, color: "#C04040", display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 15 }}>🚨</span>
            <span>Medical emergency? <strong>Call 911 immediately.</strong> MedGuide is guidance only — not emergency response.</span>
          </div>
        </div>

        {/* Chat window */}
        <div style={{
          width: "100%", maxWidth: 660,
          background: "rgba(255,255,255,0.76)", backdropFilter: "blur(16px)",
          borderRadius: 24, boxShadow: "0 8px 48px rgba(26,53,48,0.1),0 2px 8px rgba(26,53,48,0.06)",
          border: "1px solid rgba(190,220,213,0.5)",
          display: "flex", flexDirection: "column", overflow: "hidden",
          minHeight: 500, maxHeight: "calc(100vh - 215px)",
        }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "24px 22px 8px" }}>
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              return (
                <div key={i} className="msg" style={{
                  display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 18,
                }}>
                  {!isUser && (
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: "linear-gradient(135deg,#3D9E87,#2A7A65)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 16, flexShrink: 0, marginRight: 10, marginTop: 2,
                      boxShadow: "0 2px 8px rgba(45,122,101,0.3)",
                    }}>🫀</div>
                  )}
                  <div style={{
                    maxWidth: "76%", padding: "13px 18px",
                    borderRadius: isUser ? "20px 20px 5px 20px" : "20px 20px 20px 5px",
                    background: isUser ? "linear-gradient(135deg,#2A7A65,#3D9E87)" : "rgba(255,255,255,0.95)",
                    color: isUser ? "#fff" : "#1A3530",
                    fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-wrap", fontWeight: 400,
                    boxShadow: isUser ? "0 4px 16px rgba(42,122,101,0.3)" : "0 2px 12px rgba(0,0,0,0.07)",
                    border: isUser ? "none" : "1px solid rgba(180,215,205,0.4)",
                  }}>
                    {m.content}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: "linear-gradient(135deg,#3D9E87,#2A7A65)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 16, flexShrink: 0, marginRight: 10,
                }}>🫀</div>
                <div style={{
                  background: "rgba(255,255,255,0.95)", borderRadius: "20px 20px 20px 5px",
                  border: "1px solid rgba(180,215,205,0.4)",
                }}>
                  <Dots />
                </div>
              </div>
            )}

            {!started && !loading && (
              <div style={{ marginTop: 6, marginBottom: 6, animation: "fi 0.5s ease 0.4s both" }}>
                <p style={{ fontSize: 12, color: "#8AADA6", marginBottom: 10, textAlign: "center", fontStyle: "italic" }}>
                  Not sure how to start? Try one of these:
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
                  {STARTERS.map((s, i) => (
                    <button key={i} className="sb" onClick={() => send(s)} style={{
                      background: "rgba(61,158,135,0.07)", border: "1px solid #C0D9D2",
                      borderRadius: 20, padding: "7px 14px", fontSize: 12.5, color: "#3A7A70",
                      cursor: "pointer", fontFamily: "'Inter',sans-serif",
                      transition: "all 0.2s", fontStyle: "italic",
                    }}>"{s}"</button>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div style={{ height: 1, background: "linear-gradient(90deg,transparent,rgba(180,215,205,0.5),transparent)" }} />

          <div style={{ padding: "13px 16px 16px", display: "flex", gap: 10, alignItems: "flex-end" }}>
            <div style={{
              flex: 1, background: "rgba(240,248,245,0.8)",
              border: "1.5px solid #C0D9D2", borderRadius: 16, padding: "10px 14px",
              transition: "border-color 0.2s, box-shadow 0.2s",
            }}>
              <textarea
                ref={taRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Tell me what you've been feeling…"
                rows={1}
                style={{
                  width: "100%", border: "none", background: "transparent",
                  fontSize: 14.5, color: "#1A3530", lineHeight: 1.6,
                  maxHeight: 130, overflowY: "auto", display: "block",
                }}
                onFocus={e => {
                  e.target.parentElement.style.borderColor = "#3D9E87";
                  e.target.parentElement.style.boxShadow = "0 0 0 3px rgba(61,158,135,0.1)";
                }}
                onBlur={e => {
                  e.target.parentElement.style.borderColor = "#C0D9D2";
                  e.target.parentElement.style.boxShadow = "none";
                }}
              />
            </div>
            <button
              className="sndbtn"
              onClick={() => send()}
              disabled={!canSend}
              style={{
                width: 44, height: 44, borderRadius: "50%",
                background: canSend ? "linear-gradient(135deg,#3D9E87,#2A7A65)" : "#D5E8E3",
                border: "none", cursor: canSend ? "pointer" : "not-allowed",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all 0.2s",
                boxShadow: canSend ? "0 4px 14px rgba(42,122,101,0.3)" : "none",
              }}
            >
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13" stroke={canSend ? "#fff" : "#9ABFB8"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke={canSend ? "#fff" : "#9ABFB8"} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <p style={{
          marginTop: 14, fontSize: 11.5, color: "#8AADA6",
          textAlign: "center", maxWidth: 520, lineHeight: 1.6, fontStyle: "italic",
        }}>
          MedGuide offers general health guidance only. It is not a licensed medical provider and cannot diagnose or treat any condition. Always consult a qualified healthcare professional.
        </p>
      </div>
    </>
  );
}
